import { useBreakpoint } from "@stackmorph/hooks";
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  VisuallyHidden,
} from "@stackmorph/ui";
import { cn } from "@stackmorph/utils";
import { Outlet } from "react-router";

import { useBuilderStore } from "@/client/stores/builder";

import { BuilderHeader } from "./_components/header";
import { BuilderToolbar } from "./_components/toolbar";
import { LeftSidebar } from "./sidebars/left";
import { RightSidebar } from "./sidebars/right";

const onOpenAutoFocus = (event: Event) => {
  event.preventDefault();
};

const OutletSlot = () => (
  <>
    <div className="absolute inset-0">
      <Outlet />
    </div>

    <BuilderToolbar />
  </>
);

export const BuilderLayout = () => {
  const { isDesktop } = useBreakpoint();

  const sheet = useBuilderStore((state) => state.sheet);

  const leftSetSize = useBuilderStore((state) => state.panel.left.setSize);
  const rightSetSize = useBuilderStore((state) => state.panel.right.setSize);

  const leftPanel = useBuilderStore((state) => state.panel.left);
  const rightPanel = useBuilderStore((state) => state.panel.right);

  const leftHandle = useBuilderStore((state) => state.panel.left.handle);
  const rightHandle = useBuilderStore((state) => state.panel.right.handle);

  if (isDesktop) {
    return (
      <div className="flex h-screen flex-col overflow-hidden bg-background">
        <BuilderHeader />
        
        <div className="relative flex-1 overflow-hidden">
          <PanelGroup direction="horizontal">
            {leftPanel.open && (
              <>
                <Panel
                  minSize={25}
                  maxSize={45}
                  defaultSize={30}
                  className={cn("z-10 bg-background", !leftHandle.isDragging && "transition-[flex]")}
                  onResize={leftSetSize}
                >
                  <LeftSidebar />
                </Panel>
                <PanelResizeHandle
                  isDragging={leftHandle.isDragging}
                  onDragging={leftHandle.setDragging}
                />
              </>
            )}
            <Panel>
              <OutletSlot />
            </Panel>
            {rightPanel.open && (
              <>
                <PanelResizeHandle
                  isDragging={rightHandle.isDragging}
                  onDragging={rightHandle.setDragging}
                />
                <Panel
                  minSize={25}
                  maxSize={45}
                  defaultSize={30}
                  className={cn("z-10 bg-background", !rightHandle.isDragging && "transition-[flex]")}
                  onResize={rightSetSize}
                >
                  <RightSidebar />
                </Panel>
              </>
            )}
          </PanelGroup>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-background">
      <BuilderHeader />

      <div className="relative flex-1">
        <Sheet open={sheet.left.open} onOpenChange={sheet.left.setOpen}>
          <VisuallyHidden>
            <SheetHeader>
              <SheetTitle />
              <SheetDescription />
            </SheetHeader>
          </VisuallyHidden>

          <SheetContent
            side="left"
            showClose={false}
            className="top-16 p-0 sm:max-w-xl"
            onOpenAutoFocus={onOpenAutoFocus}
          >
            <LeftSidebar />
          </SheetContent>
        </Sheet>

        <OutletSlot />

        <Sheet open={sheet.right.open} onOpenChange={sheet.right.setOpen}>
          <SheetContent
            side="right"
            showClose={false}
            className="top-16 p-0 sm:max-w-xl"
            onOpenAutoFocus={onOpenAutoFocus}
          >
            <VisuallyHidden>
              <SheetHeader>
                <SheetTitle />
                <SheetDescription />
              </SheetHeader>
            </VisuallyHidden>

            <RightSidebar />
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
};
